// src/lib/tombstones.js
// 2026-05-29 EXTRACTED from src/App.jsx (C1 refactor — soft-delete / tombstone
// tracking cluster) as a behavior-preserving TRANSFORM.
//
// Why extract:
//   - These three containers + their helpers are the delete/tombstone tracking
//     state shared across the network layer (apiGet / apiSave / connectWebSocket /
//     flushPendingSaves) AND many page-level components (Deals, CRGDeals,
//     CustomerPayments, Dashboard, FtdsInfo, DailyCalcs, Invoice) that call
//     trackDelete on user delete. Keeping them inside the 6741-LOC App.jsx
//     monolith forces every reader/writer to live in-file (or import from
//     App.jsx). Hoisting them into a tiny module-singleton lets the network layer
//     + pages import the SAME state без App.jsx coupling — same precedent as
//     src/lib/connection-state.js and src/lib/local-storage.js.
//
// Read/write contract (ESM live bindings):
//   - All three trackers (deletedIDs, persistDeletedIDs, tombstonedIDs) are
//     `const` PLAIN OBJECTS, NEVER reassigned — only mutated in place
//     (X[table] = new Set(); X[table].add/.delete/.clear; delete X[table]).
//     The imported reference points at the SAME object across modules, so NO
//     setter is needed (and adding one would be a footgun — they MUST stay single
//     shared instances, like the old in-file `const deletedIDs = {}`). A duplicate
//     object would silently split delete-tracking between modules.
//
// Threading model: single-threaded JS — no race on these mutations.

import { lsGet, lsSave, LS_DELETED_IDS_KEY, LS_TOMBSTONED_IDS_KEY } from './local-storage.js';
// PR R (sub_R3, 28.09): единое определение защищённой DC-строки — то же, что сервер
// (guard общего POST + dc-field) — см. releaseProtectedDcMarks ниже.
import { isDeleteProtectedDcRow } from '../../shared/dc-row-protection.js';

// ── Durable persistence for the resurrection-guard trackers (audit F2, 2026-06-04) ──
// persistDeletedIDs + tombstonedIDs are the ONLY thing merge-by-id.js has to stop the
// server re-adding a row the user deleted (a deleted money row "coming back" after a
// reload). They USED to live only in memory, so a page reload reset them to {} — and if a
// delete never reached the server (HTTP-409 conflict, or an offline delete dropped by
// flushPendingSaves after its retries) OR the server pruned its tombstone echo, then after
// reload mergeByID saw the still-present server row, found it in none of the (now-empty)
// deleted sets, and RESURRECTED it. We now mirror both trackers into localStorage — the
// same load/save-on-mutation pattern as src/lib/data-versions.js — so deletes survive a
// reload. On-disk shape (JSON): { "<table>": ["<id>", ...], ... } (Sets stored as arrays).
//
// deletedIDs (the pending-not-yet-SENT queue, drained by getAndClearDeletes on each save)
// is deliberately NOT persisted: it is ephemeral by design, and everything trackDelete puts
// in it is ALSO copied into the durable persistDeletedIDs below — so the resurrection guard
// is fully covered WITHOUT re-driving stale server deletes from a previous session (which
// could wrongly nuke a record legitimately re-created with the same id). See getAndClearDeletes.

// Hydrate a tracker object ({ table: Set }) IN PLACE from a localStorage key. MUST mutate
// `target` (never reassign) — the exported trackers are shared singletons that merge-by-id.js
// imports by reference; replacing the reference would silently split delete-tracking between
// modules (see the Read/write contract above). Merges into any existing Set, so it is safe to
// call after entries already exist. `export` for unit testing.
export function hydrateDeletedIDs(target, lsKey) {
  try {
    const stored = localStorage.getItem(lsKey);
    if (!stored) return;
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object') return;
    for (const table of Object.keys(parsed)) {
      const ids = parsed[table];
      if (!Array.isArray(ids)) continue;
      if (!target[table]) target[table] = new Set();
      for (const id of ids) target[table].add(id);
    }
  } catch (e) { /* no localStorage / corrupt JSON — start empty, never throw */ }
}

// Serialize a tracker object ({ table: Set }) back to localStorage as { table: [id,...] }.
// Empty tables are omitted to keep the blob lean. `export` for unit testing.
export function saveDeletedIDs(source, lsKey) {
  try {
    const plain = {};
    for (const table of Object.keys(source)) {
      const set = source[table];
      if (set && set.size > 0) plain[table] = Array.from(set);
    }
    localStorage.setItem(lsKey, JSON.stringify(plain));
  } catch (e) { /* localStorage full / unavailable — best-effort, never throw */ }
}

// ── Track explicit deletes per table so server-side merge can honor them ──
export const deletedIDs = {}; // { 'payments': Set(['id1','id2']), ... } — EPHEMERAL pending-send queue (NOT persisted)
// v9.15: Persistent deleted IDs — survives merge cycles so Telegram offers don't re-add deleted deals.
// 2026-06-04 (audit F2): now ALSO survives a page reload — hydrated from localStorage just below.
export const persistDeletedIDs = {};
// v10.05: Track tombstoned IDs per table — records the server has permanently deleted.
// 2026-06-04 (audit F2): now ALSO survives a page reload — hydrated from localStorage just below.
export const tombstonedIDs = {}; // { 'payments': Set(['id1','id2']), ... }

// 2026-06-04 (audit F2): hydrate the two DURABLE trackers from localStorage IN PLACE
// (mutate the shared singletons, do NOT reassign) so user deletes survive a reload.
hydrateDeletedIDs(persistDeletedIDs, LS_DELETED_IDS_KEY);
hydrateDeletedIDs(tombstonedIDs, LS_TOMBSTONED_IDS_KEY);

export function trackDelete(table, id) {
  if (!deletedIDs[table]) deletedIDs[table] = new Set();
  deletedIDs[table].add(id);
  if (!persistDeletedIDs[table]) persistDeletedIDs[table] = new Set();
  persistDeletedIDs[table].add(id);
  // 2026-06-04 (audit F2): mirror the durable set to localStorage so this delete survives a
  // page reload. deletedIDs (the ephemeral send-queue) is intentionally NOT persisted.
  saveDeletedIDs(persistDeletedIDs, LS_DELETED_IDS_KEY);
}
// 2026-09-27 (T13 bulk, фронт) — оптимистичное атомарное удаление ДО ответа сервера:
// строку прячем (persistDeletedIDs), но в очередь `deleted` НЕ ставим. Очередь сливает
// любое legacy-сохранение таблицы (автосейв после самого же удаления строки), и сервер
// удалил бы строку без сверки версии раньше, чем ответит version_conflict. В очередь
// id кладёт trackDelete — только на ветке legacy-фолбэка или после подтверждения.
export function hideDelete(table, id) {
  if (!persistDeletedIDs[table]) persistDeletedIDs[table] = new Set();
  persistDeletedIDs[table].add(id);
  saveDeletedIDs(persistDeletedIDs, LS_DELETED_IDS_KEY);
}
// 2026-09-27 (T13 bulk, фронт) — обратная к trackDelete: сервер удаление ОТКАЗАЛ
// (version_conflict и т.п.), клиент возвращает строку. Без неё метка оставалась: строка
// пряталась из следующих ответов сервера, а очередь deletedIDs уходила следующим
// legacy-сохранением как `deleted` — и сервер удалял строку, не сверяя версию.
// Подтверждённый сервером tombstone (tombstonedIDs) не трогаем: там правда за сервером.
export function untrackDelete(table, id) {
  if (deletedIDs[table]) deletedIDs[table].delete(id);
  const confirmedGone = tombstonedIDs[table] && tombstonedIDs[table].has(id);
  if (!confirmedGone && persistDeletedIDs[table] && persistDeletedIDs[table].delete(id)) {
    saveDeletedIDs(persistDeletedIDs, LS_DELETED_IDS_KEY);
  }
}
function isDeleted(table, id) {
  return (persistDeletedIDs[table] && persistDeletedIDs[table].has(id)) ||
         (tombstonedIDs[table] && tombstonedIDs[table].has(id));
}

// v31.09 (2026-04-21): Merge Telegram offers into deals с compound key (affiliate, country).
// Старая логика (merge by id only) позволяла duplicates накапливаться — каждый bot update
// генерировал новые id для той же (aff, country) пары → добавлялись как new records, старые
// не удалялись. User видел 535 deals вместо ~484 unique pairs.
// Новая логика: группируем существующие deals по (aff, country), для каждой пары держим
// самый свежий. Новые offers от bot'а либо добавляют новую пару, либо REPLACE existing
// (если новая запись свежее по updatedAt/createdAt/date).
// Returns: {merged: Array, added: N, replaced: N}
// `export` added 2026-05-29 to enable unit testing of this dedup logic (no behavior
// change — already used internally by the Telegram-offer merge path).
export function mergeOffersIntoDeals(existingDeals, newOffers) {
  const keyOf = d => `${String(d.affiliate || '').trim().toLowerCase()}|${String(d.country || '').trim().toUpperCase()}`;
  const getTs = d => {
    const u = Number(d.updatedAt); if (u) return u;
    const c = Number(d.createdAt); if (c) return c;
    const dt = d.date ? Date.parse(d.date) : NaN;
    return Number.isFinite(dt) ? dt : 0;
  };
  const byKey = new Map();
  // Index existing deals. Если внутри deals уже есть duplicate (legacy) — keep latest.
  for (const d of (existingDeals || [])) {
    if (!d || !d.id || isDeleted('deals', d.id)) continue;
    const k = keyOf(d);
    if (!k || k === '|') continue; // skip records без aff+country (bad data)
    const prev = byKey.get(k);
    if (!prev || getTs(d) > getTs(prev)) byKey.set(k, d);
  }
  let added = 0, replaced = 0;
  for (const o of (newOffers || [])) {
    if (!o || !o.id || isDeleted('deals', o.id)) continue;
    const k = keyOf(o);
    if (!k || k === '|') continue;
    const existing = byKey.get(k);
    if (!existing) { byKey.set(k, o); added++; }
    else if (getTs(o) >= getTs(existing)) { byKey.set(k, o); replaced++; }
  }
  return { merged: Array.from(byKey.values()), added, replaced };
}

// v10.05: Process tombstoned IDs from server response — purge from local state
// `export` added 2026-05-29 for unit testing (no behavior change — used internally
// by apiGet/apiSave success paths).
export function processTombstoned(table, tombstoned) {
  if (!Array.isArray(tombstoned) || tombstoned.length === 0) return;
  if (!tombstonedIDs[table]) tombstonedIDs[table] = new Set();
  tombstoned.forEach(id => tombstonedIDs[table].add(id));
  // Also add to persistDeletedIDs so isDeleted() catches them
  if (!persistDeletedIDs[table]) persistDeletedIDs[table] = new Set();
  tombstoned.forEach(id => persistDeletedIDs[table].add(id));
  // Remove from pending deletes — server already knows about these
  if (deletedIDs[table]) {
    tombstoned.forEach(id => deletedIDs[table].delete(id));
  }
  // 2026-06-04 (audit F2): persist BOTH durable trackers so server-confirmed tombstones
  // survive a page reload (closes the resurrection class for the already-round-tripped path too).
  saveDeletedIDs(tombstonedIDs, LS_TOMBSTONED_IDS_KEY);
  saveDeletedIDs(persistDeletedIDs, LS_DELETED_IDS_KEY);
  // Clean localStorage — remove tombstoned records
  const local = lsGet(table, null);
  if (Array.isArray(local)) {
    const filtered = local.filter(r => !tombstonedIDs[table].has(r.id));
    if (filtered.length < local.length) {
      lsSave(table, filtered);
      console.log(`🪦 [${table}]: Purged ${local.length - filtered.length} tombstoned records from localStorage`);
    }
  }
}

// v10.05: Filter tombstoned records from any data array
// 2026-08-02 (прод-инцидент CRG Deals: удалённые строки возвращались на экран):
// добавлен ephemeral deletedIDs. tombstonedIDs заполняется ТОЛЬКО из серверного
// подтверждения (processTombstoned) — до его прихода строка, только что удалённая
// в этом браузере, не фильтровалась ничем, и отставший снапшот (WS-эхо предыдущей
// операции / задержавшийся poll-GET) возвращал её листенерам. mergeByID (poll-путь)
// проверял все три трекера с самого начала — этот фильтр отставал от него, отчего
// баг вылезал только на WS-пути.
// deletedIDs, а НЕ persistDeletedIDs: durable-трекер переживает reload и чистится
// только revive-веткой, у которой для не-canary таблиц (crg-deals) нет входных
// данных (msg.tombstoned не шлётся) — фильтр по нему прятал бы восстановленную
// админом строку НАВСЕГДА, т.е. воспроизводил баг, закрытый 2026-07-17.
// deletedIDs дренируется в processTombstoned, как только сервер подтвердит delete.
//
// ГРАНИЦА ПРИМЕНИМОСТИ (жюри B, 2026-08-02) — почему этот фильтр НЕ универсальная
// защита и почему это нормально. Реальное окно «удалено локально ↔ сервер подтвердил»
// он закрывает только там, где deletedIDs доживает до ответа сервера, т.е. на row-пути
// (apiSaveRows → flushRowQueue; сегодня это только crg-deals). На full-array пути
// apiSave осушает очередь синхронно ДО fetch (api-client.js:492 против :498), так что
// к моменту ответа она пуста. Остальным таблицам фильтр и не нужен: их WS-сообщения
// идут через mergeByID (App.jsx:1969), который проверяет ВСЕ ТРИ трекера, включая
// durable persistDeletedIDs — а его getAndClearDeletes не трогает (см. его коммент).
// Дыра была ровно у тех вкладок, что применяют серверный массив в обход mergeByID:
// crg-deals (инцидент) и selling-offers (App.jsx:1943 — та же архитектура, симптом
// пока не ловили). Если сюда однажды заведут таблицу с деньгами — она унаследует
// окно row-пути, и вот тогда persistDeletedIDs-слой придётся достраивать иначе.
export function filterTombstoned(table, data) {
  if (!Array.isArray(data)) return data;
  const tomb = tombstonedIDs[table];
  const pending = deletedIDs[table];
  const hasTomb = tomb && tomb.size > 0;
  const hasPending = pending && pending.size > 0;
  if (!hasTomb && !hasPending) return data;
  return data.filter(r => !(hasTomb && tomb.has(r.id)) && !(hasPending && pending.has(r.id)));
}

// NOTE (audit F2): this drains ONLY the ephemeral deletedIDs send-queue. The durable
// persistDeletedIDs (and its localStorage mirror) is intentionally left intact — that is
// what keeps a deleted row from being resurrected after the save round-trips / after a reload.
export function getAndClearDeletes(table) {
  const ids = deletedIDs[table] ? Array.from(deletedIDs[table]) : [];
  if (deletedIDs[table]) deletedIDs[table].clear();
  return ids;
}

// ── 2026-07-17 — reconcile/revive восстановленных строк (tombstone-контракт) ──────────
// БАГ: tombstonedIDs никогда не чистился (.delete/.clear по нему отсутствовали во всём
// src/) и переживал reload через localStorage — браузер, видевший delete до admin-restore,
// прятал восстановленную строку НАВСЕГДА. processTombstoned вдобавок зеркалит id в
// persistDeletedIDs, поэтому isDeleted() скрывал строку во всех merge-путях.
// ROOT CAUSE: контракт был односторонним — сервер умел только ДОБАВЛЯТЬ id в клиентский
// кэш удалений; канала «строка снова живая» не существовало.
// ФИКС: syncServerTombstones — единая точка обработки tombstone-aware сообщения
// (GET envelope crud-get/simple-get и WS-бродкаст canary-таблиц). Маркер aware-канала —
// САМО НАЛИЧИЕ массива `tombstoned` в сообщении: такие ответы сервер строит после
// server-side фильтра реестра, поэтому присутствие строки в data = авторитетное
// «строка живая» (admin-restore уже вычистил её id из реестра — см. removeTombstones).
//
// Порядок внутри СТРОГО фиксирован (контр-примеры запинены в tombstone-helpers.test.js):
//   1) processTombstoned(deleted) + processTombstoned(tombstoned) — добавления, как раньше;
//   2) revive-кандидат: id есть в data ∧ id ∈ tombstonedIDs[table] ∧ id ∉ union(tombstoned,
//      deleted) ЭТОГО ЖЕ сообщения (конфликт → прячем, безопасный дефолт) ∧ id не висит в
//      ephemeral deletedIDs[table] (несент delete = активное намерение юзера — не отменяем;
//      persistDeletedIDs-only записи, т.е. свои НЕподтверждённые delete, кандидатами не
//      становятся вовсе — требуется членство в tombstonedIDs = server-confirmed класс).
//      Revive чистит ОБА durable-трекера + их localStorage-зеркала;
//   3) filterTombstoned(data) ПОСЛЕДНИМ — revived-строки доходят до листенеров/страниц.
//
// Защита от stale-сообщений (задержавшийся GET после WS-эха delete, буфер после
// reconnect): session-scoped floor версий. СОЗНАТЕЛЬНО не персистится: floor — только
// упорядочивание внутри вкладки; персистентный floor после сброса versions на сервере
// навсегда заблокировал бы reconcile (вернул бы исходный баг «скрыто навсегда»).
// Второй слой защиты — server-side: даже проскочивший stale-revive умирает на следующем
// сейве (merge-канарейка дропает tombstoned id по реестру) и переэхивается tombstoned'ом.
export const tableVersionFloor = {}; // { table: number } — max версия, виденная в ЭТОЙ сессии

export function syncServerTombstones(table, { data, tombstoned, deleted, version } = {}) {
  processTombstoned(table, deleted);
  processTombstoned(table, tombstoned);

  // Floor поднимаем на КАЖДОМ сообщении с числовой версией (и без tombstoned-поля тоже):
  // любое виденное состояние таблицы — точка отсчёта «не старее».
  let versionOk = true;
  const v = Number(version);
  if (Number.isFinite(v) && v > 0) {
    const floor = tableVersionFloor[table];
    if (floor !== undefined && v < floor) versionOk = false;
    else tableVersionFloor[table] = v;
  }

  const revived = [];
  if (versionOk && Array.isArray(data) && Array.isArray(tombstoned)
      && tombstonedIDs[table] && tombstonedIDs[table].size > 0) {
    const serverDead = new Set([...tombstoned, ...(Array.isArray(deleted) ? deleted : [])]);
    for (const r of data) {
      const id = r?.id;
      if (id == null) continue;
      if (!tombstonedIDs[table].has(id)) continue;
      if (serverDead.has(id)) continue;
      if (deletedIDs[table] && deletedIDs[table].has(id)) continue;
      tombstonedIDs[table].delete(id);
      if (persistDeletedIDs[table]) persistDeletedIDs[table].delete(id);
      revived.push(id);
    }
    if (revived.length > 0) {
      saveDeletedIDs(tombstonedIDs, LS_TOMBSTONED_IDS_KEY);
      saveDeletedIDs(persistDeletedIDs, LS_DELETED_IDS_KEY);
      console.log(`♻️ [${table}]: revived ${revived.length} restored row(s): ${revived.slice(0, 5).join(', ')}${revived.length > 5 ? '…' : ''}`);
    }
  }

  return { data: filterTombstoned(table, data), revived };
}

// ── PR R (sub_R3, 2026-09-28) — Daily Calcs delete-protection vs. stale local marks ──
// R5 (PR R) makes a subset of brand rows delete-protected server-side (anchored /
// archived / carrying Balance Yesterday — shared/dc-row-protection.js
// isDeleteProtectedDcRow, mirrored by backend/services/daily-calcs-post-guard.cjs). A
// browser tab that marked such a row deleted BEFORE it became protected (or before this
// guard shipped) left a mark in persistDeletedIDs that nothing would ever clear: unlike
// a server-confirmed tombstone (tombstonedIDs, reconciled by syncServerTombstones's
// revive branch above), an unsent/rejected client-only delete mark has no server signal
// to key a revive off — the row simply stays hidden in THIS browser forever, even
// though the server has never agreed it is gone and never will (it now refuses to
// delete it at all).
//
// Only persistDeletedIDs is touched, and only when the id is:
//   - present among `serverRows` (the row is actually still alive on the server) AND
//     now delete-protected (isDeleteProtectedDcRow) — an ordinary deleted row must
//     stay hidden;
//   - NOT in the ephemeral deletedIDs send-queue — a delete THIS tab just queued (about
//     to be sent, or mid-flight) is a live intent, not a stale mark;
//   - NOT in tombstonedIDs — a server-confirmed tombstone is a fact this function
//     cannot and must not second-guess.
//
// Call this BEFORE merge-by-id.js's persistDeletedIDs filter runs, i.e. before
// syncServerTombstones/filterTombstoned are applied to the SAME response — see the two
// call sites in api-client.js (apiGet, gated on `endpoint === 'daily-calcs-data'`; the
// WS onmessage handler, gated on `msg.table === 'daily-calcs-data'`).
//
// @param {Array<object>} serverRows raw daily-calcs-data rows from the server (GET
//   `data` or WS `msg.data`), BEFORE any tombstone/delete filtering.
// @returns {boolean} true if any mark was released (useful for tests/diagnostics).
export function releaseProtectedDcMarks(serverRows) {
  const table = 'daily-calcs-data';
  const marks = persistDeletedIDs[table];
  if (!marks || marks.size === 0 || !Array.isArray(serverRows)) return false;
  const queued = deletedIDs[table];
  const tombstoned = tombstonedIDs[table];
  let released = false;
  for (const row of serverRows) {
    const id = row && row.id;
    if (id == null || !marks.has(id)) continue;
    if (queued && queued.has(id)) continue;
    if (tombstoned && tombstoned.has(id)) continue;
    if (!isDeleteProtectedDcRow(row)) continue;
    marks.delete(id);
    released = true;
  }
  if (released) saveDeletedIDs(persistDeletedIDs, LS_DELETED_IDS_KEY);
  return released;
}
